import { useGT } from 'gt-react';
import { ChartScatterIcon } from '@phosphor-icons/react';
import { reportNumberFormatOptions } from '@erp/shared-types';
import { defineComponentType } from '../registry';
import { useReportDataset } from '../ReportDataContext';
import type { ScatterChartComponent } from '../types';
import {
  ColumnSelect,
  ConfigRow,
  DatasetSelectRow,
  ToggleRow,
  valueByCategoryTitle,
  ChartFrame,
  useXYChart,
  useChartFit,
} from './shared';
import { NO_AXIS } from './chartConstants';
import { thinScatterPoints } from './density';
import { numericPoints } from './chartRows';
import { BuiltinChart } from './BuiltinChart';
import type { BuiltinChartRequest } from './viz/request';

/** A scatter's axes read as plain Numbers, as a Number column does. */
const PLAIN_NUMBER = reportNumberFormatOptions(undefined);

function ScatterChartRenderer({
  component,
}: {
  component: ScatterChartComponent;
}) {
  const { width, height } = useChartFit();
  const { dataset, ready, xKey, yKey, xLabel, yLabel } = useXYChart(component);

  const points =
    ready && dataset ? numericPoints(dataset.rows, xKey, yKey) : [];
  // Past a few thousand points, one per few pixels looks the same and draws
  // in a fraction of the time.
  const drawn = thinScatterPoints(points, xKey, yKey, { width, height });
  const request: BuiltinChartRequest = {
    kind: 'scatter',
    options: {
      data: drawn,
      x: xKey,
      y: yKey,
      xTitle: xLabel,
      yTitle: yLabel,
      xFormat: PLAIN_NUMBER,
      yFormat: PLAIN_NUMBER,
      grid: component.showGrid,
    },
  };

  return (
    <ChartFrame
      ready={ready}
      hasData={dataset !== null}
      icon={<ChartScatterIcon className="h-8 w-8 text-muted-foreground/40" />}
    >
      <BuiltinChart request={request} />
    </ChartFrame>
  );
}

function ScatterChartConfigForm({
  component,
  onUpdate,
}: {
  component: ScatterChartComponent;
  onUpdate: (updated: ScatterChartComponent) => void;
}) {
  const gt = useGT();
  const dataset = useReportDataset(component.datasetKey);
  const allColumns = dataset?.columns ?? [];
  const numericColumns = allColumns.filter((c) => c.dataType === 'number');
  const colOptions =
    numericColumns.length > 0
      ? numericColumns
      : [{ key: NO_AXIS, label: gt('No numeric columns in result') }];

  return (
    <div className="space-y-4">
      <DatasetSelectRow
        component={component}
        onDatasetChange={(datasetKey) =>
          onUpdate({ ...component, datasetKey, xAxisKey: null, yAxisKey: null })
        }
      />
      <ConfigRow label={gt('X-axis (numeric)')}>
        <ColumnSelect
          value={component.xAxisKey}
          columns={colOptions}
          placeholder={gt('X-axis column…')}
          onChange={(k) => onUpdate({ ...component, xAxisKey: k })}
        />
      </ConfigRow>
      <ConfigRow label={gt('Y-axis (numeric)')}>
        <ColumnSelect
          value={component.yAxisKey}
          columns={colOptions}
          placeholder={gt('Y-axis column…')}
          onChange={(k) => onUpdate({ ...component, yAxisKey: k })}
        />
      </ConfigRow>
      <ToggleRow
        label={gt('Show grid lines')}
        id="scatter-grid"
        checked={component.showGrid}
        onCheckedChange={(v) => onUpdate({ ...component, showGrid: v })}
      />
    </div>
  );
}

export function ScatterChartPalettePreview() {
  return (
    <div className="flex h-24 items-center justify-center text-foreground">
      <svg viewBox="0 0 44 36" className="h-16 w-auto" fill="none">
        <line
          x1="7"
          y1="4"
          x2="7"
          y2="30"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <line
          x1="7"
          y1="30"
          x2="40"
          y2="30"
          stroke="currentColor"
          strokeWidth="1"
          strokeOpacity="0.3"
        />
        <circle cx="13" cy="25" r="2.5" fill="currentColor" />
        <circle cx="19" cy="14" r="2" fill="currentColor" />
        <circle cx="24" cy="22" r="3" fill="currentColor" />
        <circle cx="29" cy="8" r="2" fill="currentColor" />
        <circle cx="33" cy="28" r="2.5" fill="currentColor" />
        <circle cx="38" cy="17" r="2" fill="currentColor" />
      </svg>
    </div>
  );
}

export const scatterChartDefinition =
  defineComponentType<ScatterChartComponent>({
    type: 'scatter-chart',
    label: 'Scatter chart',
    defaultTitle: valueByCategoryTitle('xAxisKey', 'yAxisKey', 'Scatter chart'),
    description:
      'Dataset-bound scatter plot of raw rows: numeric xAxisKey vs numeric yAxisKey, one point per row.',
    columnRefFields: ['xAxisKey', 'yAxisKey'],
    version: 1,
    make: (id) => ({
      type: 'scatter-chart',
      id,
      xAxisKey: null,
      yAxisKey: null,
      showGrid: true,
    }),
    Renderer: ScatterChartRenderer,
    ConfigForm: ScatterChartConfigForm,
    Preview: ScatterChartPalettePreview,
  });
